/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActivatedRoute, Router } from '@angular/router';
import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import {
    tap,
    take,
    switchMap,
    catchError,
    debounceTime,
    distinctUntilChanged,
} from 'rxjs/operators';
import * as moment from 'moment';
import { concat, Observable, of, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import {
    Constants,
    QwykValidators,
    MasterDataService,
    AlgoliaLocationsService,
} from '@qwyk/core';
import { masterdata, ui, Portals, common } from '@qwyk/models';
import { QuotationsFacade } from '@qwyk/shared-stores/quotations';
import {
    Animations,
    LocationCodePipe,
    VolumeDimensionsPipe,
} from '@qwyk/ui-standalone';

import { QuotationsService } from '../../services/quotations.service';
import { deserializeLocationCode } from '../../utils/location-code.utils';
import { mapFormToQuotationPayload } from '../../mappers/quotation.mapper';

@Component({
    selector: 'qwyk-quotation-create',
    templateUrl: './quotation-create.component.html',
    styleUrls: ['./quotation-create.component.scss'],
    animations: [Animations.fadeInAnimation],
})
export class QuotationCreateComponent implements OnInit {
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private service = inject(QuotationsService);
    private translate = inject(TranslateService);
    private quotations = inject(QuotationsFacade);
    private algolia = inject(AlgoliaLocationsService);
    private masterdataService = inject(MasterDataService);
    private readonly locationCodePipe = inject(LocationCodePipe);
    private readonly volumeDimensionsPipe = inject(VolumeDimensionsPipe);

    public readonly TRANSPORT_MODE = common.TransportMode;
    public readonly LOCATION_TYPE = common.LocationType;
    public readonly LOAD_TYPE = common.LoadType;
    public loadTypes = [
        { value: this.LOAD_TYPE.FCL, label: 'common.load-types.full-load' },
        { value: this.LOAD_TYPE.LCL, label: 'common.load-types.partial-load' },
    ];
    public transportModes = {
        lse: [{ value: this.TRANSPORT_MODE.AIR, label: 'common.transport-modes.air' }],
        fcl: [
            { value: this.TRANSPORT_MODE.OCEAN, label: 'common.transport-modes.ocean' },
            { value: this.TRANSPORT_MODE.TRUCK, label: 'common.transport-modes.truck' },
        ],
        lcl: [
            { value: this.TRANSPORT_MODE.AIR, label: 'common.transport-modes.air' },
            { value: this.TRANSPORT_MODE.OCEAN, label: 'common.transport-modes.ocean' },
            { value: this.TRANSPORT_MODE.TRUCK, label: 'common.transport-modes.truck' },
        ],
    };

    public incoTerms = Constants.INCO_TERMS;
    public minCargoReadyDate = moment().toDate();
    public isSearchingLocations: boolean;
    public locationsSuggestions: any[];
    public headerCompleted = false;
    public customersLoading = false;
    public form: FormGroup;
    public autorate = true;
    public dimensionsInput = true;
    public error = null;
    public busy = false;
    public autorating = false;
    public dateFormat: string;
    public cargoreadyPlaceholder: string;

    public packagings$: Observable<any[]>;
    public customers$: Observable<any[]>;
    public customerInput$ = new Subject<string>();
    public networkedOrganizations$ = this.service
        .getNetworkedOrganizations()
        .pipe(tap(data => (this.networkedOrganizations = data)));
    public containerTypes$ = this.masterdataService.getTransportModeLoadType(
        this.TRANSPORT_MODE.OCEAN,
        this.LOAD_TYPE.FCL
    );
    public currencies$ = this.masterdataService.getCurrencies();
    public commodities$ = this.masterdataService.getCommodities();

    private networkedOrganizations: any[];

    get getCargoFormArray() {
        return this.form.get('packages') as FormArray;
    }

    get isFcl(): boolean {
        const value = this.form.get('header.load_type')?.value;
        return this.LOAD_TYPE.FCL === value;
    }

    get isLclOrLse(): boolean {
        const value = this.form.get('header.load_type')?.value;
        return [this.LOAD_TYPE.LCL, this.LOAD_TYPE.LSE].includes(value);
    }

    get isOriginAPlace(): boolean {
        const value = this.form.get('header.origin_type')?.value;
        return value === this.LOCATION_TYPE.PLACE;
    }

    get isOriginAPort(): boolean {
        const value = this.form.get('header.origin_type')?.value;
        return value === this.LOCATION_TYPE.PORT;
    }

    get isDestinationAPlace(): boolean {
        const value = this.form.get('header.destination_type')?.value;
        return value === this.LOCATION_TYPE.PLACE;
    }

    get isDestinationAPort(): boolean {
        const value = this.form.get('header.destination_type')?.value;
        return value === this.LOCATION_TYPE.PORT;
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            header: this.fb.group({
                owner_id: [null],
                is_network_quotation: [false],
                organization_network_authorizations_id: [
                    null,
                    [
                        QwykValidators.requiredIfOtherValidator(
                            'is_network_quotation',
                            true
                        ),
                    ],
                ],
                include_own_rates: [false],
                load_type: [null, [Validators.required]],
                transport_mode: [null, [Validators.required]],
                origin: [null, [Validators.required]],
                origin_type: [this.LOCATION_TYPE.PORT, [Validators.required]],
                origin_address: [
                    null,
                    [
                        QwykValidators.requiredIfOtherValidator(
                            'origin_type',
                            this.LOCATION_TYPE.PLACE
                        ),
                    ],
                ],
                destination: [null, [Validators.required]],
                destination_type: [this.LOCATION_TYPE.PORT, [Validators.required]],
                destination_address: [
                    null,
                    [
                        QwykValidators.requiredIfOtherValidator(
                            'destination_type',
                            this.LOCATION_TYPE.PLACE
                        ),
                    ],
                ],
                require_origin_charges: [false, [Validators.required]],
                require_destination_charges: [false, [Validators.required]],
                require_freight_charges: [true, [Validators.required]],
            }),
            commodity: [null, [Validators.required]],
            inco_term: [null],
            cargo_ready: [new Date(), [Validators.required]],
            currency: [null, [Validators.required]],
            owner_reference: [null, [Validators.maxLength(255)]],
            packages: this.fb.array([], [Validators.minLength(1)]),
            additionals: this.fb.group({
                customs_clearance_origin: [false],
                customs_clearance_destination: [false],
                cargo_insurance: [false],
                hazardous_cargo: [false],
                liftgate_pickup: [false],
                refrigerated: [false],
                white_glove: [false],
                residential_delivery: [false],
                residential_pickup: [false],
                liftgate_delivery: [false],
                call_before_delivery: [false],
                delivery_appointment_required: [false],
                flatbed_required: [false],
                limited_access_delivery: [false],
                limited_access_pickup: [false],
                stackable: [false],
                overweight: [false],
            }),
        });

        this.dateFormat = `${this.translate.instant(
            'common.primeng.dateFormat'
        )}yy`;
        this.cargoreadyPlaceholder = `${this.dateFormat} hh:mm`;

        this.loadCustomers();
        this.customerInput$.next(' ');
        if (this.route.snapshot.queryParamMap.has('copy')) {
            this.upsertFromQuotation(this.route.snapshot.paramMap.get('id'));
        }
    }

    public createQuotation() {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }

        this.error = null;
        this.busy = true;
        this.form.disable();

        const loadTypeField = this.form.get('header.load_type');

        if (this.getTransportMode() === this.TRANSPORT_MODE.AIR) {
            loadTypeField.setValue(this.LOAD_TYPE.LSE);
        }

        const payload = mapFormToQuotationPayload(
            this.form.getRawValue(),
            this.isLclOrLse,
            this.dimensionsInput,
            this.locationCodePipe,
            this.volumeDimensionsPipe
        );

        this.service
            .createQuotation(payload, this.autorate)
            .pipe(take(1))
            .subscribe({
                next: quotation => {
                    this.router.navigate(
                        this.autorate
                            ? ['/', 'quotations', quotation.id]
                            : [
                                  '/',
                                  'quotations',
                                  quotation.id,
                                  'offers',
                                  'new',
                              ],
                        {
                            relativeTo: this.route,
                            queryParams: this.autorate ? { poll: true } : {},
                        }
                    );
                },
                error: error => {
                    this.form.enable();
                    this.busy = false;
                    this.error = error;
                },
            });
    }

    completeHeader(packages?: Portals.QuotationPackage[]) {
        if (this.form.get('header').invalid) {
            return;
        }
        const transportModeFieldValue = this.getTransportMode();
        const loadTypeField = this.form.get('header.load_type');

        this.packagings$ = this.masterdataService.getPackagings(
            transportModeFieldValue,
            transportModeFieldValue === this.TRANSPORT_MODE.AIR
                ? this.LOAD_TYPE.LSE
                : loadTypeField.value,
            true
        );
        this.getCargoFormArray.clear();
        if (packages?.length) {
            packages.forEach(() => {
                this.getCargoFormArray.push(
                    loadTypeField.value === this.LOAD_TYPE.FCL
                        ? this.makeFclCargoRow()
                        : this.makeLclCargoRow()
                );
            });
        } else {
            this.getCargoFormArray.push(
                loadTypeField.value === this.LOAD_TYPE.FCL
                    ? this.makeFclCargoRow()
                    : this.makeLclCargoRow()
            );
        }
        this.headerCompleted = true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onLoadTypeChanged(_e) {
        if (this.form.get('header.transport_mode').value === this.TRANSPORT_MODE.AIR) {
            this.form.get('header.transport_mode').reset();
        }
    }

    getTransportMode(): string {
        return this.form.get('header.transport_mode').value;
    }

    isOceanTransport(): boolean {
        return this.getTransportMode() === this.TRANSPORT_MODE.OCEAN;
    }

    onUOMSet(index: number, value: any) {
        const control = this.form.get(`packages.${index}.uom`);
        if (value) {
            control.setValue(value.key);
        } else {
            control.setValue(null);
        }
    }

    onVolumeChange(index: number, event): void {
        const control = this.form.get(`packages.${index}`);
        const value = event.target.value;

        const dim = this.volumeDimensionsPipe.transform(
            value,
            'volumeToDimensions',
            control.get('uom').value
        );

        ['unit_length', 'unit_width', 'unit_height'].forEach(ctrl => {
            control.get(ctrl).setValue(dim);
        });
    }

    public onLocationsAutocomplete(event: string): void {
        if (event) {
            this.isSearchingLocations = true;
            const transportMode = this.form.get('header.transport_mode').value;
            let prefer = [];
            if (transportMode === this.TRANSPORT_MODE.AIR) {
                prefer = ['is_major_airport:true', 'is_airport:true'];
            } else if (transportMode === this.TRANSPORT_MODE.OCEAN) {
                prefer = ['is_major_port:true', 'is_port:true'];
            }
            const subscr = this.algolia
                .getLocationSuggestions(event, prefer)
                .subscribe({
                    next: result => {
                        this.locationsSuggestions = result.slice(0, 5);
                        this.isSearchingLocations = false;
                        subscr.unsubscribe();
                    },
                    error: () => {
                        this.isSearchingLocations = false;
                        subscr.unsubscribe();
                    },
                });
        } else {
            this.locationsSuggestions = null;
        }
    }

    public addCargoRow() {
        (this.form.get('packages') as FormArray).push(this.makeLclCargoRow());
    }

    public removeCargoRow(idx: number) {
        const arr = this.form.get('packages') as FormArray;
        if (arr.length > 1) {
            arr.removeAt(idx);
        }
    }

    public currencySearchFn(term: string, item: masterdata.Currency) {
        term = term.toLowerCase();
        return (
            item.name.toLowerCase().indexOf(term) > -1 ||
            item.code.toLowerCase().indexOf(term) > -1
        );
    }

    public onPlaceAutocomplete(
        event: ui.GooglePlacesAddress,
        control: 'header.origin_address' | 'header.destination_address'
    ) {
        this.form.get(control).patchValue(event);
    }

    public onIsNetworkQuotationChanged(e) {
        if (!e.target.checked) {
            return;
        }

        if (!this.networkedOrganizations) {
            return;
        }

        const originValue = this.form.get('header.origin').value;
        const destinationValue = this.form.get('header.destination').value;

        if (!originValue && !destinationValue) {
            return;
        }
        let found = null;
        for (const location of [originValue, destinationValue]) {
            if (!location.country_code) {
                continue;
            }

            found = this.networkedOrganizations.find(
                e =>
                    e &&
                    e.country &&
                    e.country.iso_3166_2 === location.country_code
            );

            if (found) {
                break;
            }
        }

        if (found) {
            this.form
                .get('header.organization_network_authorizations_id')
                .setValue(found.id);
        }
    }

    private upsertFromQuotation(fromQuotationId: string): void {
        this.quotations.selectQuotation(fromQuotationId);

        this.quotations.selectedQuotations$
            .pipe(take(1))
            .subscribe(quotation => {
                if (!quotation) {
                    return;
                }
                if (quotation.request.load_type === this.LOAD_TYPE.LSE) {
                    this.loadTypes = [
                        {
                            value: this.LOAD_TYPE.LSE,
                            label: 'common.load-types.partial-load',
                        },
                    ];
                }
                this.loadCustomers(quotation.owner.email);
                this.form.patchValue({
                    header: {
                        owner_id: quotation.owner.id,
                        load_type: quotation.request.load_type,
                        transport_mode: quotation.request.transport_mode,
                        origin_type: quotation.request.origin.type,
                        origin: {
                            locode: quotation.request.origin.locode,
                            display_name: quotation.request.origin.name,
                            country_code: quotation.request.origin.country_code,
                        },
                        destination_type: quotation.request.destination.type,
                        destination: {
                            locode: quotation.request.destination.locode,
                            display_name: quotation.request.destination.name,
                            country_code:
                                quotation.request.destination.country_code,
                        },
                        origin_address:
                            quotation.request.origin.type === this.LOCATION_TYPE.PLACE
                                ? deserializeLocationCode(
                                      quotation.request.origin.code,
                                      quotation.request.origin.name
                                  )
                                : null,
                        destination_address:
                            quotation.request.destination.type === this.LOCATION_TYPE.PLACE
                                ? deserializeLocationCode(
                                      quotation.request.destination.code,
                                      quotation.request.destination.name
                                  )
                                : null,
                        require_origin_charges:
                            quotation.request.origin.type === this.LOCATION_TYPE.PLACE
                                ? true
                                : quotation.request.require_origin_charges,
                        require_freight_charges:
                            quotation.request.require_freight_charges,
                        require_destination_charges:
                            quotation.request.destination.type === this.LOCATION_TYPE.PLACE
                                ? true
                                : quotation.request.require_destination_charges,
                    },
                });
                this.completeHeader(quotation.request?.packages || []);
                const packages = quotation.request.packages.map(p => ({
                    ...p,
                    container_type: p.container_type_code,
                }));

                this.form.patchValue({
                    inco_term: quotation.request.inco_term,
                    commodity: quotation.request.commodity,
                    currency: quotation.request.currency,
                    packages,
                    additionals: quotation.request.additionals,
                });
            });
    }

    private loadCustomers(customer = null) {
        this.customers$ = concat(
            this.service.findUser(customer),
            this.customerInput$.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                tap(() => (this.customersLoading = true)),
                switchMap(term =>
                    this.service.findUser(term).pipe(
                        catchError(() => of([])),
                        tap(() => (this.customersLoading = false))
                    )
                )
            )
        );
    }

    private makeFclCargoRow(): FormGroup {
        return this.fb.group({
            quantity: [1, [Validators.required, Validators.min(1)]],
            packaging: [null, [Validators.required]],
            unit_weight: [null],
            unit_volume: [null],
            unit_width: [null],
            unit_length: [null],
            unit_height: [null],
            uom: [null],
            container_type: [null],
        });
    }

    private makeLclCargoRow(): FormGroup {
        return this.fb.group({
            quantity: [1, [Validators.required, Validators.min(1)]],
            packaging: [null, [Validators.required]],
            unit_weight: [null, [Validators.required, Validators.min(0.1)]],
            unit_volume: [null, Validators.max(100000)],
            unit_length: [
                null,
                [Validators.required, Validators.min(1), Validators.max(10000)],
            ],
            unit_width: [
                null,
                [Validators.required, Validators.min(1), Validators.max(10000)],
            ],
            unit_height: [
                null,
                [Validators.required, Validators.min(1), Validators.max(10000)],
            ],
            uom: ['metric', [Validators.required]],
        });
    }
}
